import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { useAvisoMenus, useEnviarAviso, usePreviaAviso } from "../hooks/use-aviso-em-massa";
import { AVISO_STATUS_LABEL, AvisoFiltro, AvisoStatus } from "../types";

const MAX_CHARS = 2000;

// Textos prontos para os avisos mais comuns; o atendente pode editar antes de enviar.
const MODELOS = [
  {
    rotulo: "Relatórios atrasados",
    texto:
      "Os relatórios com os valores das corridas estão atrasados e estamos aguardando a chegada. Assim que tivermos, iremos enviar as ofertas.",
  },
];

// Início de hoje, no fuso do navegador, no formato do <input type="datetime-local">.
export const inicioDoDia = (agora = new Date()): string => {
  const d = new Date(agora);
  d.setHours(0, 0, 0, 0);
  const dois = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}T${dois(d.getHours())}:${dois(d.getMinutes())}`;
};

const STATUS_OPCOES: AvisoStatus[] = ["waiting_agent", "triage", "open"];

interface AvisoEmMassaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AvisoEmMassaDialog({ open, onOpenChange }: AvisoEmMassaDialogProps) {
  const [menu, setMenu] = useState("");
  const [desde, setDesde] = useState(() => inicioDoDia());
  const [statuses, setStatuses] = useState<AvisoStatus[]>(["waiting_agent", "triage"]);
  const [texto, setTexto] = useState("");

  const { data: menus = [] } = useAvisoMenus();
  const previa = usePreviaAviso();
  const { estado, iniciar, parar, reiniciar } = useEnviarAviso();

  const enviando = estado.fase === "enviando";
  const concluido = estado.fase === "concluido";
  const contagem = previa.data;

  const filtro = (): AvisoFiltro => ({ menu, since: new Date(desde).toISOString(), statuses });

  // Mudou o público: a contagem mostrada deixa de valer.
  useEffect(() => {
    previa.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, desde, statuses]);

  const fechar = (aberto: boolean) => {
    if (enviando) return; // não fecha no meio do envio
    if (!aberto) {
      previa.reset();
      reiniciar();
    }
    onOpenChange(aberto);
  };

  const alternarStatus = (s: AvisoStatus) =>
    setStatuses((atual) => (atual.includes(s) ? atual.filter((x) => x !== s) : [...atual, s]));

  const podeContar = Boolean(menu) && statuses.length > 0 && !Number.isNaN(Date.parse(desde)) && !previa.isPending;
  const podeEnviar = Boolean(contagem?.total) && texto.trim().length > 0 && !enviando;
  const menuEscolhido = menus.find((m) => m.id === menu);

  return (
    <Dialog open={open} onOpenChange={fechar}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Aviso em massa</DialogTitle>
          <DialogDescription>
            Manda uma mensagem para todos que chegaram por um item do menu e encerra os atendimentos em seguida.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="aviso-menu">Quem chegou pelo menu</Label>
            <Select value={menu} onValueChange={setMenu} disabled={enviando || concluido}>
              <SelectTrigger id="aviso-menu">
                <SelectValue placeholder="Escolha o item do menu" />
              </SelectTrigger>
              <SelectContent>
                {menus.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="aviso-desde">Desde</Label>
            <input
              id="aviso-desde"
              type="datetime-local"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              disabled={enviando || concluido}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
            <p className="text-xs text-muted-foreground">Atendimentos movimentados desde este horário (no máximo 3 dias).</p>
          </div>

          <fieldset className="space-y-1" disabled={enviando || concluido}>
            <legend className="text-sm font-medium">Incluir</legend>
            {STATUS_OPCOES.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={statuses.includes(s)} onChange={() => alternarStatus(s)} />
                {AVISO_STATUS_LABEL[s]}
              </label>
            ))}
            {statuses.includes("open") && (
              <p className="text-xs text-amber-600">
                Quem já está em atendimento também receberá o aviso e terá a conversa encerrada.
              </p>
            )}
          </fieldset>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="aviso-texto">Mensagem</Label>
              <div className="flex gap-1">
                {MODELOS.map((m) => (
                  <Button
                    key={m.rotulo}
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={enviando || concluido}
                    onClick={() => setTexto(m.texto)}
                  >
                    {m.rotulo}
                  </Button>
                ))}
              </div>
            </div>
            <Textarea
              id="aviso-texto"
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, MAX_CHARS))}
              rows={4}
              disabled={enviando || concluido}
              placeholder="O que o entregador vai ler"
            />
            <p className="text-xs text-muted-foreground">
              O entregador vê a mensagem como "Suporte", se abrir a página do atendimento (o link vale 14 horas).
            </p>
          </div>

          {!enviando && !concluido && (
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" disabled={!podeContar} onClick={() => previa.mutate(filtro())}>
                {previa.isPending ? "Contando…" : "Contar atendimentos"}
              </Button>
              {contagem && (
                <p className="text-sm">
                  <b>{contagem.total}</b> {contagem.total === 1 ? "atendimento" : "atendimentos"}
                  {contagem.total > 0 &&
                    ` (${STATUS_OPCOES.filter((s) => (contagem.by_status[s] ?? 0) > 0)
                      .map((s) => `${contagem.by_status[s]} ${AVISO_STATUS_LABEL[s].toLowerCase()}`)
                      .join(", ")})`}
                </p>
              )}
            </div>
          )}

          {enviando && (
            <div className="space-y-2 rounded-md border bg-muted/40 p-3" role="status">
              <div className="h-2 w-full overflow-hidden rounded bg-muted">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.min(100, ((estado.processados / (contagem?.total || 1)) * 100) | 0)}%` }}
                />
              </div>
              <p className="text-sm">
                Enviando e encerrando… {estado.encerrados} de {contagem?.total ?? "?"}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={parar}>
                Parar depois deste lote
              </Button>
            </div>
          )}

          {concluido && (
            <div className="space-y-1 rounded-md border bg-muted/40 p-3" role="status">
              <p className="flex items-center gap-2 text-sm font-medium">
                {estado.erro || estado.falhas.length > 0 || estado.parouAntes ? (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
                {estado.parouAntes ? "Envio interrompido" : "Envio concluído"}
              </p>
              <p className="text-sm">
                {estado.encerrados} enviados e encerrados
                {estado.ignorados > 0 && ` · ${estado.ignorados} já estavam encerrados`}
                {estado.falhas.length > 0 && ` · ${estado.falhas.length} com erro`}
              </p>
              {estado.erro && <p className="text-sm text-destructive">{estado.erro}</p>}
              {(estado.falhas.length > 0 || estado.parouAntes) && (
                <p className="text-xs text-muted-foreground">
                  Os que ficaram continuam abertos. Conte de novo e envie: quem já recebeu não recebe outra vez.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {concluido ? (
            <Button type="button" onClick={() => fechar(false)}>
              Fechar
            </Button>
          ) : (
            <Button
              type="button"
              disabled={!podeEnviar}
              onClick={() => iniciar(filtro(), texto.trim())}
              title={contagem?.total ? undefined : "Conte os atendimentos antes de enviar"}
            >
              {enviando
                ? "Enviando…"
                : contagem?.total
                  ? `Enviar e encerrar ${contagem.total} ${contagem.total === 1 ? "atendimento" : "atendimentos"}`
                  : "Enviar e encerrar"}
            </Button>
          )}
        </DialogFooter>
        {menuEscolhido && !concluido && !enviando && contagem?.total ? (
          <p className="text-xs text-muted-foreground">
            Não dá para desfazer: a mensagem é enviada e os atendimentos de "{menuEscolhido.label}" são encerrados.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
